import {ArrowLeft, Headphones, Mail, ShieldCheck} from 'lucide-react';
import {Link, useNavigate} from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {Button} from '../components/ui/button';

const SUPPORT_EMAIL = (process.env.REACT_APP_SUPPORT_EMAIL || 'support@cultureken.com').trim();

const FAQS = [
  {
    question: 'How do I create an audio guide?',
    answer: 'Open the Map, select a place, and choose Get Audio Guide. Sign in or register if prompted, select the audio language, then use a credit. The guide will appear in your Library while it is being prepared.',
  },
  {
    question: 'Where can I find a guide I already purchased?',
    answer: 'Open Library and refresh the page or app. Check your collections as well as the main Audio Guides list. Make sure you are signed in with the same account used for the purchase. Selecting the place on the Map should show Play Audio Guide instead of asking you to purchase it again.',
  },
  {
    question: 'What should I do if guide generation fails?',
    answer: 'Try the request again after checking your internet connection. If the place repeatedly fails or a credit was deducted without a playable guide being created, contact support with the place name, approximate time of the request, and your account email.',
  },
  {
    question: 'Why will an audio guide not play?',
    answer: 'Confirm that your device is online, then close and reopen the guide. If playback still fails, restart the app or browser and try again. When contacting support, include the guide name, selected language, device type, and a screenshot of any error.',
  },
  {
    question: 'How do credits and purchases work?',
    answer: 'Credits are used to unlock audio guides. Mobile prices and currency are supplied by the App Store or Google Play for your store region. If a completed store purchase is not reflected in your balance, refresh the Credits page and contact support with the store receipt or order reference if it remains missing.',
  },
  {
    question: 'Can I listen in another language?',
    answer: 'Choose the language before generating or unlocking a guide. Your Library shows the language attached to each guide. A version in another language may need to be unlocked separately.',
  },
  {
    question: 'How do I reset my password or access my account?',
    answer: 'Use Forgot password on the sign-in screen and follow the link sent to your email address. Also check your spam or junk folder. If you registered with Google, use the same Google account when signing in.',
  },
  {
    question: 'How do I report incorrect information or an image?',
    answer: 'Contact support with the exact place name, its location, and the chapter or image concerned. Include a screenshot and a link to a reliable source when possible so the report can be investigated efficiently.',
  },
  {
    question: 'How can I delete my account or review the privacy policy?',
    answer: 'The Profile area links to the Privacy Policy and Terms of Use. Mobile users can also choose Delete account in Profile, which opens the secure account-deletion page. Deletion permanently removes your guides, credits, saved trips, collections, and progress.',
  },
];

const SupportPage = () => {
  const navigate = useNavigate();
  const contactHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Culture Ken support request')}`;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="support-page">
      <header className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-3xl items-center gap-3 px-4">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            data-testid="support-page-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <img src="/icon-192.png" alt="" className="h-8 w-8 rounded-md" />
          <span className="font-semibold">Culture Ken Support</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10 pb-20">
        <div className="mb-9">
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10">
            <Headphones className="h-5 w-5 text-primary" aria-hidden="true" />
          </div>
          <h1 className="text-3xl font-bold sm:text-4xl">How can we help?</h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-muted-foreground">
            Find answers to essential questions about accounts, audio guides, credits, playback, and your data.
          </p>
        </div>

        <section aria-labelledby="support-faq-heading">
          <h2 id="support-faq-heading" className="text-lg font-semibold">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-3 border-t" data-testid="support-faq-list">
            {FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`faq-${index}`}>
                <AccordionTrigger className="py-5 text-base">{faq.question}</AccordionTrigger>
                <AccordionContent className="pr-8 text-sm leading-6 text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section className="mt-10 border-t pt-8" aria-labelledby="contact-support-heading">
          <div className="flex items-start gap-3">
            <Mail className="mt-1 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <div>
              <h2 id="contact-support-heading" className="text-lg font-semibold">Still need help?</h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Email us with your account email, device type, place or guide name, and any relevant screenshot. Never send your password or full payment-card details.
              </p>
              <Button asChild className="mt-4" data-testid="support-email-link">
                <a href={contactHref}>Email {SUPPORT_EMAIL}</a>
              </Button>
            </div>
          </div>
        </section>

        <nav className="mt-10 flex flex-wrap gap-x-5 gap-y-3 border-t pt-6 text-sm" aria-label="Support policies">
          <Link className="inline-flex items-center gap-2 text-primary hover:underline" to="/privacy">
            <ShieldCheck className="h-4 w-4" /> Privacy Policy
          </Link>
          <Link className="text-primary hover:underline" to="/terms">Terms of Use</Link>
          <Link className="text-primary hover:underline" to="/account-deletion">Delete account</Link>
        </nav>
      </main>
    </div>
  );
};

export default SupportPage;
